/**
 * O IO da Central de Professores — zero regra.
 *
 * Desde 21/09/2026 a **Central de Professores** (o outro projeto Supabase da
 * casa, `robarnjwtonvjnglthwq`) é a fonte da verdade do cadastro de professor,
 * e toda edição feita no hub tem de escrever LÁ. Este módulo é a única forma
 * de o front alcançá-la, e ele não alcança direto: tudo passa pela Edge
 * Function `professores-da-central`, a porta única entre os dois projetos.
 *
 * Por que não um `supabase.from(...)` apontando para a Central: o segredo que
 * abre aquele projeto é a chave de SERVIÇO de lá, que ignora a RLS inteira. Ela
 * nunca sai do servidor — o bundle é público.
 *
 * O que a porta devolve é a JUNÇÃO de dois lugares, e a tela mostra os dois:
 * quem a pessoa É vem da Central; quantas disciplinas ela tem, em que cursos e
 * se está projetada no hub vêm daqui.
 *
 * **O nível mudou de natureza em 22/09/2026.** Até 21/09 ele era só derivado
 * das formações da Central, e a tela não o escolhia. Victor pediu campo
 * dedicado *"por ser o campo que aparece no certificado"*, então agora:
 * `titulacao` (a afirmação da casa) manda, e a derivação é a reserva de quem
 * ainda não tem afirmação nenhuma. A tela EDITA a titulação e MOSTRA as duas
 * respostas quando elas discordam — quem decide continua sendo gente.
 *
 * O que NÃO existe aqui, de propósito: apagar (a Central apaga em 17 tabelas por
 * CASCADE; o hub arquiva) e mexer em FORMAÇÃO — ela é a declaração do professor
 * sobre o que estudou, e se edita na ficha da Central.
 */
import { supabase } from "./supabase";

export type NivelDerivado = "especialista" | "mestre" | "doutor";

/** De onde saiu o nível que vai impresso. Ver o cabeçalho da porta. */
export type OrigemDoNivel =
  /** `professores.titulacao` preenchida — alguém da casa decidiu. */
  | "casa"
  /** Campo nulo, e as formações registradas dizem mestre ou doutor. */
  | "formacoes"
  /** Campo nulo e nenhuma formação: é o padrão, não uma afirmação. */
  | "padrao";

/** Uma linha da lista: a Central + o que o hub sabe dela. */
export type ProfessorDaCentral = {
  id: string;
  nome: string;
  email: string | null;
  whatsapp: string | null;
  cidade: string | null;
  estado: string | null;
  mini_bio: string | null;
  registro_profissional: string | null;
  linkedin_url: string | null;
  status: string;
  arquivado: boolean;
  created_at: string;
  updated_at: string;
  /**
   * O CAMPO da Central — a afirmação da casa, ou `null` quando ninguém disse.
   * É isto que o formulário edita; `nivel` é o resultado.
   */
  titulacao: NivelDerivado | null;
  /** O que vai IMPRESSO no certificado: a titulação, ou a reserva derivada. */
  nivel: NivelDerivado;
  nivel_texto: string;
  /** De onde `nivel` saiu — a tela diz isso, porque muda o que fazer. */
  nivel_origem: OrigemDoNivel;
  /** O que as FORMAÇÕES dizem, sempre. Existe para ser COMPARADO. */
  nivel_derivado: NivelDerivado;
  nivel_derivado_texto: string;
  /** A casa afirmou uma coisa e as formações dizem outra. */
  titulacao_diverge: boolean;
  /** Do HUB: quantas disciplinas apontam para ele, e em que cursos. */
  disciplinas: number;
  cursos: string[];
  /** O hub já tem a linha dele? Sem ela, nenhuma disciplina consegue apontar. */
  projetado: boolean;
  /** A projeção do hub tem um nível diferente do derivado hoje. */
  projecao_defasada: boolean;
};

/**
 * O que o hub pode ESCREVER. É a mesma lista fechada da function — se as duas
 * divergirem, a da function manda: campo fora dela volta em `ignorados`.
 */
export type CamposDoProfessor = Partial<Pick<ProfessorDaCentral,
  "nome" | "email" | "whatsapp" | "cidade" | "estado" | "mini_bio" | "registro_profissional"
  | "linkedin_url" | "titulacao">>;

/** O que `pos_docente_projetar` respondeu depois de a Central ser escrita. */
export type Projecao = { ok: boolean; motivo?: string; detalhe?: string; disciplinas?: number; criado?: boolean };

export type Resposta<T> = { ok: true; dados: T } | { ok: false; erro: string; detalhe?: string; motivo?: string };

async function porta<T>(corpo: Record<string, unknown>): Promise<Resposta<T>> {
  const { data, error } = await supabase.functions.invoke("professores-da-central", { body: corpo });
  if (error) {
    // A frase vem pronta da function; traduzir aqui criaria uma segunda tabela
    // de frases (a cicatriz do `adminCatalogo.ts`).
    let erro = error.message;
    let detalhe: string | undefined;
    let motivo: string | undefined;
    try {
      const j = await (error as { context?: Response }).context?.json();
      if (j?.erro) erro = j.erro;
      if (j?.detalhe) detalhe = j.detalhe;
      if (j?.motivo) motivo = j.motivo;
    } catch { /* sem corpo legível */ }
    return { ok: false, erro, detalhe, motivo };
  }
  return { ok: true, dados: data as T };
}

export async function listarProfessores(busca?: string) {
  return porta<{ professores: ProfessorDaCentral[]; total: number; sem_projecao: number }>({ acao: "listar", busca });
}

/** Cria (sem `professorId`) ou edita (com). A Central primeiro, a projeção depois. */
export async function salvarProfessor(campos: CamposDoProfessor, professorId?: string) {
  return porta<{ professor: ProfessorDaCentral; projecao: Projecao; ignorados: string[] }>({
    acao: "salvar", professor_id: professorId, campos });
}

/** Arquivar é o "desativar" desta casa: reversível, e o oposto é `false`. */
export async function arquivarProfessor(professorId: string, arquivado = true) {
  return porta<{ professor: ProfessorDaCentral; projecao: Projecao }>({ acao: "arquivar", professor_id: professorId, arquivado });
}

/** A Central inteira para dentro do hub. Roda depois do remap, e quando alguém aparece sem projeção. */
export async function sincronizarProfessores() {
  return porta<{ total: number; criados: number; atualizados: number;
    recusados: Array<{ nome: string; motivo?: string; detalhe?: string }> }>({ acao: "sincronizar" });
}
